/**
 * Desktop-only panel to manage the local folders Opal's filesystem tools may
 * access — the in-app equivalent of Claude's "Project Selection" / Codex's
 * "Workspace Linking". "Add Folder" opens the native OS picker on the machine
 * (the secure, physical-presence grant); the actual per-request access grant
 * still happens natively on the desktop side.
 */

import { useState } from 'react';
import { IconPlus, IconTrashCan } from '@optiaxiom/icons';
import {
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
  toaster,
} from '@optiaxiom/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { type DesktopWorkspace, getDesktopWorkspacesApi } from '@frontend/utils/desktop';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const WORKSPACES_QUERY_KEY = ['desktop-workspaces'];

export default function DesktopWorkspacesDialog({ open, onOpenChange }: Props) {
  const api = getDesktopWorkspacesApi();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  // Axiom's Dialog force-mounts, so key the fetch on `open` (and refetch on
  // reopen) rather than a mount-only effect.
  const { data: workspaces = [] } = useQuery({
    queryKey: WORKSPACES_QUERY_KEY,
    queryFn: async () => (api ? api.list() : []),
    enabled: open && !!api,
  });

  // Run a workspace mutation with busy state + error toast, seeding the cache
  // from the list it returns.
  const run = async (
    action: (workspaces: NonNullable<typeof api>) => Promise<DesktopWorkspace[]>,
    errorMsg: string
  ) => {
    if (!api) return;
    setBusy(true);
    try {
      queryClient.setQueryData(WORKSPACES_QUERY_KEY, await action(api));
    } catch {
      toaster.create(errorMsg, { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = () => run(a => a.addViaDialog(), 'Could not add folder');
  const handleRemove = (id: string) => run(a => a.remove(id), 'Could not remove folder');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>Local folders</DialogHeader>
        <DialogBody>
          <Flex flexDirection="column" gap="12">
            <Text color="fg.tertiary" fontSize="sm">
              Grant Opal access to folders on this computer so it can read, write, and search files
              in them. Access stays on this machine — you can revoke it anytime.
            </Text>

            {workspaces.length === 0 ? (
              <Box bg="bg.secondary" rounded="md" p="16">
                <Text color="fg.tertiary" fontSize="sm">
                  No folders granted yet. Add one to let Opal work with your local files.
                </Text>
              </Box>
            ) : (
              <Flex flexDirection="column" gap="8">
                {workspaces.map(ws => (
                  <Flex
                    key={ws.id}
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="12"
                    bg="bg.secondary"
                    rounded="md"
                    p="12"
                  >
                    <Flex flexDirection="column" gap="2" style={{ minWidth: 0 }}>
                      <Text fontWeight="600" fontSize="sm">
                        {ws.name}
                      </Text>
                      <Text
                        color="fg.tertiary"
                        fontSize="sm"
                        style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                      >
                        {ws.path}
                      </Text>
                    </Flex>
                    <Button
                      appearance="danger-outline"
                      type="button"
                      size="sm"
                      icon={<IconTrashCan />}
                      disabled={busy}
                      onClick={() => handleRemove(ws.id)}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))}
              </Flex>
            )}

            <Box>
              <Button
                appearance="default"
                type="button"
                icon={<IconPlus />}
                loading={busy}
                disabled={busy}
                onClick={handleAdd}
              >
                Add Folder…
              </Button>
            </Box>
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Button appearance="primary" type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
